import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import AppSnackbar from '../../../common/AppSnackbar';
import { useFindHazardControls } from '../Hazard.hooks';
import { HazardControl } from '../Hazard.interface';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ApiParams } from '../../../common/api/api';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import HazardCreateEntityDialog from '../dialogs/HazardCreateEntityDialog';

export interface ControlTableProps {
  hazardId: number;
  productId: number;
  apiParams: ApiParams;
  onChange: (openDialog: boolean) => void;
  open: boolean;
}

const ControlsTable: React.FC<ControlTableProps> = props => {
  const { hazardId, productId, apiParams, onChange, open } = props;
  const [openDialog, setOpenDialog] = useState<boolean>(open);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const history = useHistory();

  const {
    hazardControlList,
    isLoadingHazardControls,
    loadingHazardControlsError,
    findHazardControls
  } = useFindHazardControls(+hazardId);

  useEffect(() => {
    findHazardControls({ page, limit, order, direction, ...apiParams });
  }, [page, limit, order, direction, findHazardControls, apiParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };
  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'cause.id'}
                  direction={getSortDirection('cause.id')}
                  onClick={() => handleSort('cause.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'cause.title'}
                  direction={getSortDirection('cause.title')}
                  onClick={() => handleSort('cause.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'cause.createdBy'}
                  direction={getSortDirection('cause.createdBy')}
                  onClick={() => handleSort('cause.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoadingHazardControls && (
              <TableRow>
                <TableCell colSpan={3} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {hazardControlList &&
              hazardControlList.data.map((control: HazardControl) => (
                <TableRow
                  hover
                  key={control.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/hazards/${hazardId}/controls/${control.id}?from=controls`
                    )
                  }
                >
                  <TableCell>CONTROL-{control.id}</TableCell>
                  <TableCell>{control.title}</TableCell>
                  <TableCell>{`${control.createdBy.firstName} ${control.createdBy.lastName}`}</TableCell>
                  <TableCell>
                    {control.task.user
                      ? `${control.task.user?.firstName} ${control.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={control.task} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={hazardControlList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      {open && (
        <HazardCreateEntityDialog
          hazardId={hazardId}
          displayName={history.location.pathname.split('/')[6]}
          onCancel={() => {
            onChange(false);
            setOpenDialog(false);
          }}
          onCreated={() => {
            onChange(false);
            setOpenDialog(false);
            findHazardControls();
          }}
        />
      )}
      <AppSnackbar severity='error' open={Boolean(loadingHazardControlsError)}>
        There was an error loading the list of controls.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default ControlsTable;
